import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronRight, Trash2, Mail, Plus, Phone, ExternalLink, Paperclip, Calendar, Sparkles, X, Loader2, Archive } from 'lucide-react';
import { Lead, PipelineType, resolveStatusColor } from '../types';
import { fetchAIStatusRecommendation } from '../lib/db';
import { ColumnSetting, DEFAULT_COLUMNS, normalizeColumnPreferences } from './ColumnCustomizerModal';
import { sortLeadsByField, sortLeadsDefault } from '../lib/leadSorting';

const parseQuoteDetails = (quoteDetails: string | undefined) => {
  if (!quoteDetails) return { text: '', files: [] };
  try {
    if (quoteDetails.trim().startsWith('{')) {
      const parsed = JSON.parse(quoteDetails);
      if (typeof parsed === 'object' && parsed !== null) {
        return {
          text: parsed.text || '',
          files: Array.isArray(parsed.files) ? parsed.files : [],
        };
      }
    }
  } catch (e) {
    // Fail silently
  }
  return { text: quoteDetails, files: [] };
};

function getUrgencyAlertBadge(followUpDate?: string, completedAt?: string) {
  if (!followUpDate || completedAt) return null;
  const target = new Date(followUpDate);
  if (isNaN(target.getTime())) return null;

  const todayStr = new Date().toISOString().slice(0, 10);
  const isToday = followUpDate === todayStr;

  const diffMs = new Date().getTime() - target.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays >= 7) {
    return (
      <span className="inline-flex items-center gap-1 bg-rose-500 text-white text-[9px] font-black px-1.5 py-0.5 rounded shadow-sm animate-pulse">
        🔴 RED ALERT ({diffDays}d Overdue)
      </span>
    );
  }

  if (isToday) {
    return (
      <span className="inline-flex items-center gap-1 bg-amber-500 text-white text-[9px] font-black px-1.5 py-0.5 rounded shadow-sm">
        🟡 YELLOW ALERT (Due Today)
      </span>
    );
  }

  return null;
}

export function getRetentionBadge(firstShipmentDate?: string, isArchived?: boolean, finalFollowUpDate?: string) {
  if (!firstShipmentDate || isArchived || (finalFollowUpDate && finalFollowUpDate.trim() !== '')) return null;
  const shipmentDate = new Date(firstShipmentDate);
  if (isNaN(shipmentDate.getTime())) return null;

  const diffMs = new Date().getTime() - shipmentDate.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const daysLeft = Math.max(0, 30 - diffDays);

  return (
    <span className="inline-flex items-center gap-1 bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300 text-[9px] font-mono font-bold px-1.5 py-0.5 rounded border border-emerald-300">
      ⏱️ {daysLeft}d Follow-Up Window
    </span>
  );
}

const DEFAULT_COLUMN_WIDTHS: Record<string, number> = {
  lead: 220,
  leadName: 220,
  company: 220,
  consigneeName: 200,
  consigneeAccount: 150,
  shipperName: 200,
  shipperAccount: 150,
  careOf: 160,
  billToAccountNumber: 150,
  status: 160,
  contactPerson: 180,
  phone: 150,
  email: 200,
  owner: 180,
  leadType: 180,
  notes: 240,
  serviceTypes: 160,
  industries: 160,
  outboundStates: 150,
  outboundWarehouses: 170,
  daysOfService: 160,
  fullQuoteView: 220,
  estimatedDealValue: 170,
  lastUpdateDate: 150,
  creditAppSentAt: 140,
  rateAgreementSentAt: 150,
  internalEmailsSentAt: 150,
  firstDeliveryFollowUpAt: 160,
  nextSteps: 240,
  actions: 120,
};

interface BoardTableProps {
  key?: string;
  title: PipelineType;
  color: string;
  leads: Lead[];
  onUpdateStatus: (id: string, status: string) => void;
  onDeleteLead: (id: string) => void;
  onArchiveLead?: (id: string) => void;
  onEditLead: (lead: Lead) => void;
  onAddNewLeadToBoard: (pipeline: PipelineType) => void;
  searchQuery: string;
  onBulkUpdateStatus: (ids: string[], status: string) => void;
  onBulkDeleteLeads: (ids: string[]) => void;
  statusColors: Record<string, string>;
  onBulkEmail: (emails: string[]) => void;
  onMoveLead: (leadId: string, target: PipelineType) => void;
  statusOptions: string[];
  columnPreferences?: ColumnSetting[];
  activeSortField?: string;
  activeSortDirection?: 'asc' | 'desc';
  onHeaderSort?: (field: string) => void;
}

export default function BoardTable({
  title,
  color,
  leads,
  onUpdateStatus,
  onDeleteLead,
  onArchiveLead,
  onEditLead,
  onAddNewLeadToBoard,
  searchQuery,
  onBulkUpdateStatus,
  onBulkDeleteLeads,
  statusColors,
  onBulkEmail,
  onMoveLead,
  statusOptions,
  columnPreferences,
  activeSortField,
  activeSortDirection,
  onHeaderSort,
}: BoardTableProps) {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [selectedLeads, setSelectedLeads] = useState<string[]>([]);
  const [recommendingLeadId, setRecommendingLeadId] = useState<string | null>(null);
  const [activeRecommendation, setActiveRecommendation] = useState<{
    leadId: string;
    recommendedStatus: string;
    reasoning: string;
  } | null>(null);

  const [internalSortField, setInternalSortField] = useState<string | null>(null);
  const [internalSortDir, setInternalSortDir] = useState<'asc' | 'desc'>('asc');

  const effectiveSortField = activeSortField !== undefined ? activeSortField : internalSortField;
  const effectiveSortDir = activeSortDirection !== undefined ? activeSortDirection : internalSortDir;

  const handleHeaderSortClick = (colId: string) => {
    if (onHeaderSort) {
      onHeaderSort(colId);
    } else {
      if (internalSortField === colId) {
        setInternalSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
      } else {
        setInternalSortField(colId);
        setInternalSortDir('asc');
      }
    }
  };

  const sanitizeColumnWidths = (raw: any): Record<string, number> => {
    const sanitized: Record<string, number> = { ...DEFAULT_COLUMN_WIDTHS };
    if (raw && typeof raw === 'object') {
      for (const [key, val] of Object.entries(raw)) {
        if (typeof val === 'number' && !isNaN(val) && val >= 60 && val <= 800) {
          sanitized[key] = val;
        }
      }
    }
    return sanitized;
  };

  const [columnWidths, setColumnWidths] = useState<Record<string, number>>(() => {
    try {
      const saved = localStorage.getItem('crm_board_col_widths');
      if (saved) return sanitizeColumnWidths({ ...DEFAULT_COLUMN_WIDTHS, ...JSON.parse(saved) });
    } catch (e) {}
    return DEFAULT_COLUMN_WIDTHS;
  });

  useEffect(() => {
    const handleStorage = (e: StorageEvent) => {
      if (e.key === 'crm_board_col_widths' && e.newValue) {
        try {
          setColumnWidths(sanitizeColumnWidths({ ...DEFAULT_COLUMN_WIDTHS, ...JSON.parse(e.newValue) }));
        } catch (err) {}
      }
    };
    const handleCustomUpdate = () => {
      try {
        const saved = localStorage.getItem('crm_board_col_widths');
        if (saved) setColumnWidths(sanitizeColumnWidths({ ...DEFAULT_COLUMN_WIDTHS, ...JSON.parse(saved) }));
      } catch (err) {}
    };
    window.addEventListener('storage', handleStorage);
    window.addEventListener('crm_board_col_widths_updated', handleCustomUpdate);
    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('crm_board_col_widths_updated', handleCustomUpdate);
    };
  }, []);

  const getColumnWidthStyle = (colId: string): React.CSSProperties => {
    const rawWidth = columnWidths[colId] || DEFAULT_COLUMN_WIDTHS[colId] || 160;
    const width = Math.min(Math.max(60, rawWidth), 800);
    return {
      width: `${width}px`,
      minWidth: `${width}px`,
      maxWidth: `${width}px`,
    };
  };

  const handleResizeStart = (colId: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startWidth = columnWidths[colId] || DEFAULT_COLUMN_WIDTHS[colId] || 160;

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const deltaX = moveEvent.clientX - startX;
      const newWidth = Math.min(Math.max(60, startWidth + deltaX), 800);
      setColumnWidths((prev) => {
        const updated = { ...prev, [colId]: newWidth };
        try {
          localStorage.setItem('crm_board_col_widths', JSON.stringify(updated));
          window.dispatchEvent(new Event('crm_board_col_widths_updated'));
        } catch (err) {}
        return updated;
      });
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const userFilteredCols = (columnPreferences && columnPreferences.length > 0)
    ? normalizeColumnPreferences(columnPreferences).filter(c => c.visible)
    : DEFAULT_COLUMNS.filter(c => c.visible);

  const activeColumns = userFilteredCols.length > 0
    ? userFilteredCols
    : DEFAULT_COLUMNS.filter(c => c.visible);

  const handleGetRecommendation = async (lead: Lead) => {
    if (activeRecommendation?.leadId === lead.id) {
      setActiveRecommendation(null);
      return;
    }
    
    setRecommendingLeadId(lead.id);
    setActiveRecommendation(null);
    
    try {
      const data = await fetchAIStatusRecommendation(lead.leadNotes || lead.nextSteps || lead.followUpNotes || '', lead.status);
      setActiveRecommendation({
        leadId: lead.id,
        recommendedStatus: data.recommendedStatus,
        reasoning: data.reasoning,
      });
    } catch (err) {
      console.error(err);
      setActiveRecommendation({
        leadId: lead.id,
        recommendedStatus: lead.status,
        reasoning: (lead.leadNotes || lead.nextSteps || lead.followUpNotes)
          ? `Local Analysis: Continue following up on these action items: "${lead.leadNotes || lead.nextSteps || lead.followUpNotes}".`
          : "Please enter some notes in 'Lead Notes' first to generate an AI suggestion.",
      });
    } finally {
      setRecommendingLeadId(null);
    }
  };

  // Filter leads by search query locally across multiple fields
  let filteredLeads = leads.filter((lead) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    const leadTypeStr = Array.isArray(lead.leadType) ? lead.leadType.join(' ') : (lead.leadType || '');
    return (
      (lead.name || '').toLowerCase().includes(q) ||
      (lead.company || '').toLowerCase().includes(q) ||
      (lead.consigneeName || '').toLowerCase().includes(q) ||
      (lead.shipperName || '').toLowerCase().includes(q) ||
      (lead.careOf && lead.careOf.toLowerCase().includes(q)) ||
      (lead.billToAccountNumber && lead.billToAccountNumber.toLowerCase().includes(q)) ||
      (lead.email || '').toLowerCase().includes(q) ||
      (lead.owner && lead.owner.toLowerCase().includes(q)) ||
      (lead.contactPerson && lead.contactPerson.toLowerCase().includes(q)) ||
      (lead.service && lead.service.toLowerCase().includes(q)) ||
      (lead.industry && lead.industry.toLowerCase().includes(q)) ||
      (lead.leadNotes && lead.leadNotes.toLowerCase().includes(q)) ||
      (lead.followUpNotes && lead.followUpNotes.toLowerCase().includes(q)) ||
      (lead.nextSteps && lead.nextSteps.toLowerCase().includes(q)) ||
      leadTypeStr.toLowerCase().includes(q)
    );
  });

  if (!onHeaderSort && internalSortField) {
    filteredLeads = sortLeadsByField(filteredLeads, internalSortField, internalSortDir);
  }

  // Toggle single checkbox
  const handleSelectLead = (id: string) => {
    setSelectedLeads((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  // Toggle all checkboxes in this board
  const handleSelectAll = () => {
    const allFilteredIds = filteredLeads.map((l) => l.id);
    const areAllSelected = allFilteredIds.every((id) => selectedLeads.includes(id));

    if (areAllSelected) {
      setSelectedLeads((prev) => prev.filter((id) => !allFilteredIds.includes(id)));
    } else {
      setSelectedLeads((prev) => Array.from(new Set([...prev, ...allFilteredIds])));
    }
  };

  // Helper for background color of Monday-style status values
  const getStatusBgColor = (status: string) => {
    return resolveStatusColor(status, statusColors);
  };

  const getStatusTextColor = (status: string) => {
    return '#ffffff';
  };

  const handleDragOver = (e: React.DragEvent) => {
    if (e.dataTransfer.types.includes('application/x-lead-id')) {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      setIsDragOver(true);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const leadId = e.dataTransfer.getData('application/x-lead-id');
    if (leadId) onMoveLead(leadId, title);
  };

  return (
    <section
      className={`mb-8 rounded-xl transition-all ${isDragOver ? 'ring-2 ring-blue-500 ring-offset-2 dark:ring-offset-slate-900 bg-blue-50/50 dark:bg-blue-950/20' : ''}`}
      onDragOver={handleDragOver}
      onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setIsDragOver(false); }}
      onDrop={handleDrop}
    >
      {/* Table Header Controls */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 group cursor-pointer" onClick={() => setIsCollapsed(!isCollapsed)}>
          <button className="focus:outline-none hover:bg-slate-200 rounded p-0.5 transition" style={{ color }}>
            {isCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          <h2 className="text-[15px] font-bold" style={{ color }}>
            {title}
          </h2>
          <span className="text-xs text-slate-400 font-medium ml-1">
            {filteredLeads.length} {filteredLeads.length === 1 ? 'Item' : 'Items'}
          </span>
        </div>

        {/* Action to quickly add lead inline to this specific board */}
        <button
          onClick={() => onAddNewLeadToBoard(title)}
          className="flex items-center gap-1 text-[11px] font-bold text-slate-500 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 px-2 py-1 hover:bg-slate-100 dark:hover:bg-slate-900 rounded transition cursor-pointer"
        >
          <Plus className="w-3.5 h-3.5" /> Add to {title.split(' ')[0]}
        </button>
      </div>

      {/* Bulk Action Bar */}
      {selectedLeads.length > 0 && (
        <div className="bg-blue-50 dark:bg-blue-950/40 border-l-4 border-blue-500 p-3 mb-3 rounded-r-lg flex flex-col sm:flex-row items-center justify-between gap-4 shadow-sm animate-fade-in transition-colors">
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold text-blue-800 dark:text-blue-250 bg-blue-100 dark:bg-blue-900/60 px-2 py-0.5 rounded-full">
              {selectedLeads.length} Selected
            </span>
            <p className="text-xs font-medium text-slate-700 dark:text-slate-300">
              Apply bulk actions to selected leads in <strong style={{ color }}>{title}</strong>
            </p>
          </div>
          <div className="flex items-center gap-3">
            {/* Status Selector Dropdown */}
            <div className="flex items-center gap-1.5">
              <span className="text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase">Update Status:</span>
              <select
                onChange={(e) => {
                  if (e.target.value) {
                    onBulkUpdateStatus(selectedLeads, e.target.value);
                    setSelectedLeads([]);
                    e.target.value = '';
                  }
                }}
                className="bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-800 rounded px-2 py-1 text-xs font-bold text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-1 focus:ring-blue-500 cursor-pointer"
                defaultValue=""
              >
                <option value="" disabled>Select Status...</option>
                {statusOptions.map(opt => (
                  <option key={opt} value={opt}>{opt === 'Revisit Later | ADD FU DATE' ? 'Revisit Later' : opt}</option>
                ))}
              </select>
            </div>

            <div className="w-px h-5 bg-slate-300 dark:bg-slate-800"></div>

            {/* Bulk Delete Button */}
            <button
              onClick={() => {
                if (window.confirm(`Are you sure you want to delete these ${selectedLeads.length} selected leads?`)) {
                  onBulkDeleteLeads(selectedLeads);
                  setSelectedLeads([]);
                }
              }}
              className="flex items-center gap-1 text-xs font-bold text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 bg-red-50 dark:bg-red-950/40 hover:bg-red-100 dark:hover:bg-red-900 px-2.5 py-1.5 rounded transition cursor-pointer border border-transparent hover:border-red-200 dark:hover:border-red-900/60"
            >
              <Trash2 className="w-3.5 h-3.5" /> Delete Selected
            </button>

            {/* Bulk Email Button */}
            <button
              onClick={() => {
                const selectedObjs = leads.filter((l) => selectedLeads.includes(l.id));
                const emails = selectedObjs.map((l) => l.email).filter(Boolean);
                if (emails.length === 0) {
                  alert('None of the selected leads have an email address specified.');
                  return;
                }
                onBulkEmail(emails);
              }}
              className="flex items-center gap-1 text-xs font-bold text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 bg-blue-50 dark:bg-blue-950/40 hover:bg-blue-100 dark:hover:bg-blue-900 px-2.5 py-1.5 rounded transition cursor-pointer border border-transparent hover:border-blue-200 dark:hover:border-blue-900/60"
            >
              <Mail className="w-3.5 h-3.5" /> Bulk Email
            </button>

            {/* Clear Selection */}
            <button
              onClick={() => setSelectedLeads([])}
              className="text-xs font-bold text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-900 px-2.5 py-1.5 rounded transition cursor-pointer border border-transparent hover:border-slate-200 dark:hover:border-slate-800"
            >
              Clear
            </button>
          </div>
        </div>
      )}

      {!isCollapsed && (
        <div className="armellini-board-container flex overflow-x-auto w-full">
          <table className="armellini-board table-fixed min-w-full w-max">
            <colgroup>
              <col style={{ width: '6px', minWidth: '6px', maxWidth: '6px' }} />
              <col style={{ width: '48px', minWidth: '48px', maxWidth: '48px' }} />
              {activeColumns.map((col) => {
                const rawWidth = columnWidths[col.id] || DEFAULT_COLUMN_WIDTHS[col.id] || 160;
                const width = Math.min(Math.max(60, rawWidth), 800);
                return (
                  <col
                    key={col.id}
                    style={{ width: `${width}px`, minWidth: `${width}px`, maxWidth: `${width}px` }}
                  />
                );
              })}
            </colgroup>
            <thead>
              <tr className="bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800">
                <th className="armellini-color-bar" style={{ backgroundColor: color, borderBottomColor: color, width: '6px', minWidth: '6px', maxWidth: '6px' }}></th>
                <th className="w-12 text-center py-2 px-3" style={{ minWidth: '48px', width: '48px', maxWidth: '48px' }}>
                  <input
                    type="checkbox"
                    checked={filteredLeads.length > 0 && filteredLeads.every((l) => selectedLeads.includes(l.id))}
                    onChange={handleSelectAll}
                    className="rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                  />
                </th>

                {/* Dynamic Configurable Column Headers */}
                {activeColumns.map((col) => {
                  const isSortable = [
                    'lead', 'leadName', 'company', 'consigneeName', 'shipperName', 'careOf', 'billToAccountNumber',
                    'contactPerson', 'owner', 'serviceTypes', 'industries',
                    'outboundStates', 'outboundWarehouses', 'daysOfService', 'estimatedDealValue',
                    'lastUpdateDate', 'status', 'leadType', 'notes', 'nextSteps',
                    'creditAppSentAt', 'rateAgreementSentAt', 'internalEmailsSentAt', 'firstDeliveryFollowUpAt'
                  ].includes(col.id);
                  const isCurrentSort = effectiveSortField === col.id;

                  return (
                    <th
                      key={col.id}
                      style={getColumnWidthStyle(col.id)}
                      className={`relative group/col text-xs font-bold text-slate-500 dark:text-slate-400 py-2.5 px-3 whitespace-nowrap overflow-hidden text-ellipsis select-none ${
                        col.id === 'estimatedDealValue'
                          ? 'text-right'
                          : col.id === 'actions' || col.id === 'status'
                          ? 'text-center'
                          : 'text-left'
                      } ${isSortable ? 'cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800 transition' : ''}`}
                    >
                      <div
                        className={`flex items-center gap-1.5 whitespace-nowrap overflow-hidden text-ellipsis ${
                          col.id === 'estimatedDealValue'
                            ? 'justify-end'
                            : col.id === 'actions' || col.id === 'status'
                            ? 'justify-center'
                            : 'justify-start'
                        }`}
                        onClick={() => isSortable && handleHeaderSortClick(col.id)}
                      >
                        <span className="truncate">{col.label}</span>
                        {isSortable && (
                          <span className="text-[10px] text-slate-400 shrink-0">
                            {isCurrentSort ? (effectiveSortDir === 'asc' ? '↑' : '↓') : '↕'}
                          </span>
                        )}
                      </div>

                      {/* Column Resizer Handle */}
                      <div
                        onMouseDown={(e) => handleResizeStart(col.id, e)}
                        onClick={(e) => e.stopPropagation()}
                        className="absolute top-0 right-0 h-full w-2 cursor-col-resize hover:bg-blue-500/80 bg-transparent active:bg-blue-600 transition-colors z-20"
                        title="Drag to resize column"
                      />
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {filteredLeads.length === 0 ? (
                <tr>
                  <td className="armellini-color-bar" style={{ backgroundColor: color, width: '6px', minWidth: '6px', maxWidth: '6px' }}></td>
                  <td colSpan={activeColumns.length + 1} className="text-center py-8 text-xs text-slate-400 font-mono">
                    {searchQuery ? 'No leads matching filter' : 'No active leads in this pipeline'}
                  </td>
                </tr>
              ) : (
                filteredLeads.map((lead) => (
                  <tr
                    key={lead.id}
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.setData('application/x-lead-id', lead.id);
                      e.dataTransfer.effectAllowed = 'move';
                    }}
                    className={`group border-b border-slate-200 dark:border-slate-800 last:border-b-0 cursor-grab active:cursor-grabbing ${
                      lead.isArchived
                        ? 'bg-slate-100/70 dark:bg-slate-900/50 opacity-75 hover:bg-slate-200/60'
                        : 'hover:bg-slate-50/40 dark:hover:bg-slate-900/40'
                    }`}
                  >
                    <td className="armellini-color-bar" style={{ backgroundColor: color, width: '6px', minWidth: '6px', maxWidth: '6px' }}></td>
                    
                    <td className="text-center py-2 px-3" style={{ width: '48px', minWidth: '48px', maxWidth: '48px' }}>
                      <input
                        type="checkbox"
                        checked={selectedLeads.includes(lead.id)}
                        onChange={() => handleSelectLead(lead.id)}
                        className="rounded border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-blue-600 focus:ring-blue-500 cursor-pointer"
                      />
                    </td>

                    {/* Dynamic Configurable Column Cells */}
                    {activeColumns.map((col) => {
                      const colStyle = getColumnWidthStyle(col.id);
                      switch (col.id) {
                        case 'lead':
                        case 'leadName':
                          return (
                            <td key={col.id} style={colStyle} className="py-2 px-3">
                              <div className="flex flex-col gap-1 overflow-hidden">
                                <button
                                  onClick={() => onEditLead(lead)}
                                  className="font-bold text-slate-900 dark:text-slate-100 hover:text-blue-600 dark:hover:text-blue-400 text-left truncate transition cursor-pointer"
                                  title="Click to view details"
                                >
                                  {lead.company || lead.name || '—'}
                                </button>
                                {lead.name && lead.name !== lead.company && (
                                  <span className="text-[11px] text-slate-500 dark:text-slate-400 truncate font-medium">
                                    {lead.name}
                                  </span>
                                )}
                                <div className="flex flex-wrap items-center gap-1">
                                  {lead.isArchived && (
                                    <span className="inline-flex items-center text-[10px] font-bold text-purple-700 dark:text-purple-300 bg-purple-100 dark:bg-purple-950/60 px-1.5 py-0.5 rounded border border-purple-200 dark:border-purple-800">
                                      <Archive className="w-2.5 h-2.5 mr-1" /> Archived
                                    </span>
                                  )}
                                  {getUrgencyAlertBadge(lead.followUpDate, lead.followUpCompletedAt)}
                                  {getRetentionBadge(lead.firstShipmentDate, lead.isArchived, lead.finalFollowUpDate)}
                                  {lead.followUpDate && !getUrgencyAlertBadge(lead.followUpDate, lead.followUpCompletedAt) && (
                                    <div className="inline-flex items-center gap-1 text-[10px] font-bold text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-950/40 px-1.5 py-0.5 rounded border border-blue-100 dark:border-blue-900/40 w-fit">
                                      <Calendar className="w-2.5 h-2.5" /> FU: {lead.followUpDate}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </td>
                          );
                        case 'leadType': {
                          const types = Array.isArray(lead.leadType)
                            ? lead.leadType
                            : (lead.leadType ? [lead.leadType] : []);
                          return (
                            <td key={col.id} style={colStyle} className="py-2 px-3 text-xs">
                              {types.length > 0 ? (
                                <div className="flex flex-wrap gap-1 items-center">
                                  {types.map((t, idx) => (
                                    <span
                                      key={`${t}-${idx}`}
                                      className="inline-flex items-center text-[10px] font-semibold bg-blue-50 dark:bg-blue-950/60 text-blue-700 dark:text-blue-300 px-1.5 py-0.5 rounded border border-blue-200 dark:border-blue-800"
                                    >
                                      {t}
                                    </span>
                                  ))}
                                </div>
                              ) : (
                                <span className="text-slate-400 font-mono">—</span>
                              )}
                            </td>
                          );
                        }
                        case 'company':
                          return (
                            <td key={col.id} style={colStyle} className="py-2 px-3 text-xs font-semibold text-slate-800 dark:text-slate-200 truncate">
                              {lead.company || lead.consigneeName || '—'}
                            </td>
                          );
                        case 'consigneeName':
                          return (
                            <td key={col.id} style={colStyle} className="py-2 px-3 text-xs font-semibold text-slate-800 dark:text-slate-200 truncate">
                              {lead.consigneeName || '—'}
                            </td>
                          );
                        case 'consigneeAccount':
                          return (
                            <td key={col.id} style={colStyle} className="py-2 px-3 text-xs text-slate-600 dark:text-slate-400 font-mono truncate">
                              {lead.consigneeAccount || '—'}
                            </td>
                          );
                        case 'shipperName':
                          return (
                            <td key={col.id} style={colStyle} className="py-2 px-3 text-xs font-semibold text-slate-800 dark:text-slate-200 truncate">
                              {lead.shipperName || '—'}
                            </td>
                          );
                        case 'shipperAccount':
                          return (
                            <td key={col.id} style={colStyle} className="py-2 px-3 text-xs text-slate-600 dark:text-slate-400 font-mono truncate">
                              {lead.shipperAccount || '—'}
                            </td>
                          );
                        case 'careOf':
                          return (
                            <td key={col.id} style={colStyle} className="py-2 px-3 text-xs font-semibold text-slate-800 dark:text-slate-200 truncate">
                              {lead.careOf || '—'}
                            </td>
                          );
                        case 'billToAccountNumber':
                          return (
                            <td key={col.id} style={colStyle} className="py-2 px-3 text-xs text-slate-600 dark:text-slate-400 font-mono truncate">
                              {lead.billToAccountNumber || '—'}
                            </td>
                          );
                        case 'creditAppSentAt':
                        case 'rateAgreementSentAt':
                        case 'internalEmailsSentAt':
                        case 'firstDeliveryFollowUpAt': {
                          const dateVal = (lead as any)[col.id];
                          return (
                            <td key={col.id} style={colStyle} className="py-2 px-3 text-xs font-mono truncate">
                              {dateVal ? (
                                <span className="inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-400 font-bold">
                                  ✓ {dateVal}
                                </span>
                              ) : (
                                <span className="text-slate-400">—</span>
                              )}
                            </td>
                          );
                        }
                        case 'contactPerson':
                          return (
                            <td key={col.id} style={colStyle} className="py-2 px-3 text-xs font-semibold text-slate-800 dark:text-slate-200 truncate">
                              {lead.contactPerson || '—'}
                            </td>
                          );
                        case 'phone':
                          return (
                            <td key={col.id} style={colStyle} className="py-2 px-3 text-xs text-slate-600 dark:text-slate-400 font-mono truncate">
                              {lead.phone ? (
                                <span className="inline-flex items-center gap-1">
                                  <Phone className="w-3 h-3 opacity-60 shrink-0" /> {lead.phone}
                                </span>
                              ) : '—'}
                            </td>
                          );
                        case 'email':
                          return (
                            <td key={col.id} style={colStyle} className="py-2 px-3 text-xs text-slate-600 dark:text-slate-400 truncate">
                              {lead.email ? (
                                <a href={`mailto:${lead.email}`} className="text-blue-600 dark:text-blue-400 hover:underline inline-flex items-center gap-1 truncate">
                                  <Mail className="w-3 h-3 opacity-60 shrink-0" /> {lead.email}
                                </a>
                              ) : '—'}
                            </td>
                          );
                        case 'owner':
                          return (
                            <td key={col.id} style={colStyle} className="py-2 px-3 text-xs font-semibold text-slate-700 dark:text-slate-300 truncate">
                              {lead.owner || 'Unassigned'}
                            </td>
                          );
                        case 'serviceTypes':
                          return (
                            <td key={col.id} style={colStyle} className="py-2 px-3 text-xs font-semibold text-slate-700 dark:text-slate-300 truncate">
                              {(lead.serviceTypes && lead.serviceTypes.length > 0) ? lead.serviceTypes.join(', ') : lead.service || '—'}
                            </td>
                          );
                        case 'industries':
                          return (
                            <td key={col.id} style={colStyle} className="py-2 px-3 text-xs font-mono uppercase text-slate-500 dark:text-slate-400 truncate">
                              {(lead.industries && lead.industries.length > 0) ? lead.industries.join(', ') : lead.industry || '—'}
                            </td>
                          );
                        case 'outboundStates':
                          return (
                            <td key={col.id} style={colStyle} className="py-2 px-3 text-xs font-semibold text-slate-700 dark:text-slate-300 truncate">
                              {(lead.outboundStates && lead.outboundStates.length > 0) ? lead.outboundStates.join(', ') : lead.state || '—'}
                            </td>
                          );
                        case 'outboundWarehouses':
                          return (
                            <td key={col.id} style={colStyle} className="py-2 px-3 text-xs font-mono text-slate-600 dark:text-slate-400 truncate">
                              {(lead.outboundWarehouses && lead.outboundWarehouses.length > 0) ? lead.outboundWarehouses.join(', ') : '—'}
                            </td>
                          );
                        case 'daysOfService':
                          return (
                            <td key={col.id} style={colStyle} className="py-2 px-3 text-xs font-mono text-slate-600 dark:text-slate-400 truncate">
                              {(lead.daysOfService && lead.daysOfService.length > 0) ? lead.daysOfService.join(', ') : '—'}
                            </td>
                          );
                        case 'fullQuoteView':
                          return (
                            <td key={col.id} style={colStyle} className="py-2 px-3 text-xs font-mono italic text-slate-500 truncate" title={lead.fullQuoteView || lead.quoteDetails}>
                              {lead.fullQuoteView || parseQuoteDetails(lead.quoteDetails).text || '—'}
                            </td>
                          );
                        case 'estimatedDealValue':
                          return (
                            <td key={col.id} style={colStyle} className="font-mono text-slate-700 dark:text-slate-200 text-right py-2 px-3 font-semibold text-xs truncate">
                              ${(lead.estimatedDealValue || lead.dealValue || 0).toLocaleString()}
                            </td>
                          );
                        case 'lastUpdateDate':
                          return (
                            <td key={col.id} style={colStyle} className="py-2 px-3 text-xs text-slate-500 dark:text-slate-400 font-mono truncate">
                              {lead.lastContacted || '—'}
                            </td>
                          );
                        case 'notes':
                        case 'nextSteps': {
                          const noteContent = lead.leadNotes || lead.followUpNotes || lead.nextSteps || '';
                          return (
                            <td key={col.id} style={colStyle} className="py-2 px-3 text-xs text-slate-600 dark:text-slate-300 truncate" title={noteContent}>
                              {noteContent || '—'}
                            </td>
                          );
                        }
                        case 'status':
                          return (
                            <td key={col.id} style={colStyle} className="status-cell py-1 px-2 text-center">
                              <div
                                className="status-badge font-bold text-center text-[11px] uppercase tracking-wider rounded w-full flex items-center justify-center min-h-[34px] px-2 select-none cursor-default"
                                style={{
                                  backgroundColor: getStatusBgColor(lead.status),
                                  color: getStatusTextColor(lead.status),
                                }}
                                title="Status automatically updates based on pipeline milestone dates"
                              >
                                {lead.status === 'Revisit Later | ADD FU DATE' ? 'Revisit Later' : lead.status}
                              </div>
                            </td>
                          );
                        case 'actions':
                          return (
                            <td key={col.id} style={colStyle} className="text-center py-2 px-3">
                              <div className="flex items-center justify-center gap-1">
                                {onArchiveLead && (
                                  <button
                                    onClick={() => onArchiveLead(lead.id)}
                                    className={`p-1 rounded transition cursor-pointer ${
                                      lead.isArchived
                                        ? 'hover:bg-emerald-50 text-emerald-600'
                                        : 'hover:bg-purple-50 text-slate-400 hover:text-purple-600'
                                    }`}
                                    title={lead.isArchived ? "Restore Lead" : "Archive Lead"}
                                  >
                                    <Archive className="w-3.5 h-3.5" />
                                  </button>
                                )}
                                <button
                                  onClick={() => onEditLead(lead)}
                                  className="p-1 hover:bg-slate-200 dark:hover:bg-slate-800 text-slate-500 hover:text-slate-800 dark:hover:text-slate-200 rounded transition cursor-pointer"
                                  title="Edit Lead Details"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-pencil"><path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"/></svg>
                                </button>
                                <button
                                  onClick={() => onDeleteLead(lead.id)}
                                  className="p-1 hover:bg-red-50 dark:hover:bg-red-950/40 text-slate-400 hover:text-red-600 rounded transition cursor-pointer"
                                  title="Delete Lead"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </td>
                          );
                        default:
                          return <td key={col.id} style={colStyle} className="py-2 px-3 text-xs text-slate-500">—</td>;
                      }
                    })}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
